import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Alert, Box, Flex, Switch, Text, toaster } from '@optiaxiom/react';
import { IconDatabase } from '@optimizely/axiom-icons';

import { extractApiErrorMessage } from '@frontend/api/http';
import { instanceConfigurationService } from '@frontend/api/services/instanceConfigurationService';
import { opalUserAtom } from '@frontend/atoms';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { IconMicrochipAI } from '@frontend/components/IconMicrochipAI';
import { InferenceTypeSelector } from '@frontend/components/InferenceTypeSelector';
import { AgentInferenceType } from '@frontend/types/specialized-agent';

import { styles } from '../../Settings.css';

function ChatSetupConfiguration() {
  const user = useAtomValue(opalUserAtom);
  const [selectedInference, setSelectedInference] = useState<AgentInferenceType>('simple');
  const [enableRag, setEnableRag] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfiguration = async () => {
      if (!user?.instance?.instance_id) return;

      setIsLoading(true);
      setError(null);

      try {
        const config = await instanceConfigurationService.getInstanceConfiguration(
          user.instance.instance_id
        );
        setSelectedInference(config.default_inference_for_primary_agent);
        setEnableRag(config.enable_rag_in_primary_agent);
      } catch (err) {
        setError(extractApiErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadConfiguration();
  }, [user?.instance?.instance_id]);

  const handleSet = async (inferenceType: AgentInferenceType) => {
    if (!user?.instance?.instance_id) return;
    if (inferenceType === selectedInference) return;

    setSelectedInference(inferenceType);

    try {
      await instanceConfigurationService.updateInstanceConfiguration(user.instance.instance_id, {
        default_inference_for_primary_agent: inferenceType,
        enable_rag_in_primary_agent: enableRag,
      });
      toaster.create('Configuration updated successfully!', { intent: 'success' });
    } catch (err) {
      const msg = extractApiErrorMessage(err);
      toaster.create(msg, { intent: 'danger' });
    }
  };

  const handleEnableRagToggle = async (checked: boolean) => {
    if (!user?.instance?.instance_id) return;

    setEnableRag(checked);

    try {
      await instanceConfigurationService.updateInstanceConfiguration(user.instance.instance_id, {
        default_inference_for_primary_agent: selectedInference,
        enable_rag_in_primary_agent: checked,
      });
      toaster.create('Configuration updated successfully!', { intent: 'success' });
    } catch (err) {
      const msg = extractApiErrorMessage(err);
      toaster.create(msg, { intent: 'danger' });
      setEnableRag(!checked);
    }
  };

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (error) {
    return (
      <Box>
        <Alert intent="danger" title="Error loading configuration">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Flex flexDirection="column" gap="32" mt="8" className={styles.settingsContainer}>
      <Flex
        flexDirection="row"
        gap="8"
        alignItems="center"
        justifyContent="space-between"
        p="16"
        bg="bg.default"
        rounded="xl"
      >
        <Flex flexDirection="row" gap="12" alignItems="center">
          <Box color="fg.tertiary">
            <IconMicrochipAI size="20" />
          </Box>
          <Flex flexDirection="column" gap="2">
            <Text fontWeight="500">Inference Level</Text>
            <Text fontSize="sm" color="fg.tertiary" className={styles.settingsDescription}>
              Controls how much reasoning Opal applies when generating its response. Higher
              inference levels can take more time, but apply deeper reasoning to output
            </Text>
          </Flex>
        </Flex>
        <InferenceTypeSelector
          value={selectedInference}
          onChange={inference_type => handleSet(inference_type)}
        />
      </Flex>

      <Flex
        flexDirection="row"
        gap="8"
        alignItems="center"
        justifyContent="space-between"
        p="16"
        bg="bg.default"
        rounded="xl"
      >
        <Flex flexDirection="row" gap="12" alignItems="center">
          <Box color="fg.tertiary">
            <IconDatabase size="20" />
          </Box>
          <Flex flexDirection="column" gap="2">
            <Text fontWeight="500">Enable RAG in Chat</Text>
            <Text fontSize="sm" color="fg.tertiary" className={styles.settingsDescription}>
              Toggle to enable RAG for direct messages in opal chat. Agents can use RAG regardless
              of this setting.
            </Text>
          </Flex>
        </Flex>
        <Switch
          checked={enableRag}
          onCheckedChange={handleEnableRagToggle}
          aria-label="Toggle RAG in primary agent"
        />
      </Flex>
    </Flex>
  );
}

export default ChatSetupConfiguration;
